/**
 * The scoring brief, read-only — backend `domain/profile.py::Rubric`, served on
 * `GET /api/profile` as `row.rubric`.
 *
 * This is the *only* thing the deep-ranker sees. `build_system_prompt` in
 * `llm/prompts/rank.py` takes a `Rubric` and nothing else, so none of the
 * editable fields in `ProfileReview` above this reach the judge. Until this
 * component existed, the object that decided every match was invisible to the
 * person it described.
 *
 * Read-only on purpose. A form with a textarea per axis invites edits that read
 * as harmless and quietly break scoring — "no more selling" typed over a
 * deal-breaker deletes the language rule sitting next to it. `RefineChat` is
 * the way in: it changes one thing at a time and reports what it changed.
 *
 * LAYOUT: the job of this screen is spotting a rule that is WRONG, not reading
 * top to bottom. So it is grouped the way the rubric actually behaves rather
 * than the order the fields happen to sit in on the model:
 *
 *   - Gates decide yes or no. `must_haves` and `deal_breakers` are opposites,
 *     so they sit side by side and share one row grammar — a marker in the
 *     gutter, one line of rule, nothing else.
 *   - Dials decide how well. The three axes are one weighted rail, because the
 *     claim being made is a *comparison* ("meaning matters more to you than
 *     pay") and three separate cards make the reader do the arithmetic.
 *
 * Nothing is behind a disclosure. A rule you cannot see is a rule you cannot
 * correct, and a collapsed section on a page meant for error-spotting hides
 * exactly the thing it exists to surface.
 */

import { useI18n } from '../i18n'
import type { CommuteRule, Rubric, Strength } from '../types/domain'
import { Icon } from './Icon'
import type { IconName } from './Icon'
import './RubricView.css'

interface RubricViewProps {
  /** `null` while onboarding has produced a profile row but no rubric yet. */
  rubric: Rubric | null
}

export function RubricView({ rubric }: RubricViewProps) {
  const { t } = useI18n()

  if (!rubric) {
    return (
      <section className="rubric-view">
        <h2>{t('profile.rubricTitle')}</h2>
        <p className="rubric-view-help">{t('profile.rubricMissing')}</p>
      </section>
    )
  }

  const axes = [
    { label: t('profile.rubricAxisExperience'), axis: rubric.experience },
    { label: t('profile.rubricAxisValues'), axis: rubric.values },
    { label: t('profile.rubricAxisSalary'), axis: rubric.salary },
  ]
  const total = axes.reduce((sum, a) => sum + a.axis.weight, 0)
  // Rounded once, here, so the rail and the printed figures cannot disagree.
  const shares = axes.map((a) => (total > 0 ? Math.round((a.axis.weight / total) * 100) : 0))

  return (
    <section className="rubric-view">
      <header className="rubric-view-head">
        <div>
          <h2>{t('profile.rubricTitle')}</h2>
          <p className="rubric-view-note">{t('profile.rubricBody')}</p>
        </div>
        <span className="rubric-view-version">
          {t('profile.rubricVersion', { version: String(rubric.version) })}
        </span>
      </header>

      {/* The one sentence describing a good job for this person leads, at
          display weight. It used to sit third, under two explanatory
          paragraphs, in the same body size as everything else. */}
      {rubric.summary && <p className="rubric-view-lead">{rubric.summary}</p>}

      <div className="rubric-view-dials">
        <h3 className="eyebrow">{t('profile.rubricAxesHeading')}</h3>
        <div className="rubric-view-rail" aria-hidden="true">
          {shares.map((share, i) => (
            <span key={axes[i].label} style={{ width: `${share}%` }} />
          ))}
        </div>
        {/* A list, not an ordered one: the three axes are peers scored in
            parallel, and numbering them would claim a sequence that isn't
            there. Their position is fixed only so the rail's colours line up
            with the columns beneath it — and, now, with the notes below. */}
        <ul className="rubric-view-legend">
          {axes.map(({ label }, i) => (
            <AxisShare key={label} label={label} share={shares[i]} />
          ))}
        </ul>
        {/* PIVOT-145: the paragraph used to live inside the three-up legend
            column above, where a real LLM description (943 characters on the
            experience axis, measured live) ran to ~34 lines in a grid column
            that stretches every axis to the tallest of the three. Stacked
            here instead, each capped at a readable measure, with the
            colour-coded rule moved from the legend's `border-top` to
            `border-left` so the segment-to-axis link survives the move. */}
        <ul className="rubric-view-axis-notes">
          {axes.map(({ label, axis }) => (
            <AxisNote key={label} label={label} description={axis.description} />
          ))}
        </ul>
      </div>

      <div className="rubric-view-gates">
        <RuleList
          label={t('profile.rubricMustHaves')}
          items={rubric.must_haves.map((line) => line.text)}
          kind="require"
        />
        <RuleList
          label={t('profile.rubricDealBreakers')}
          items={rubric.deal_breakers.map((line) => line.text)}
          kind="reject"
        />
      </div>

      {rubric.strengths.length > 0 && (
        <div className="rubric-view-block">
          <h3 className="eyebrow">{t('profile.rubricStrengths')}</h3>
          <ul className="rubric-view-strengths">
            {rubric.strengths.map((s) => (
              <StrengthRow key={s.claim} strength={s} />
            ))}
          </ul>
        </div>
      )}

      {rubric.proven_but_unwanted.length > 0 && (
        <RuleList
          label={t('profile.rubricProvenUnwanted')}
          items={rubric.proven_but_unwanted}
          kind="both"
          help={t('profile.rubricProvenUnwantedHelp')}
        />
      )}

      <RuleList
        label={t('profile.rubricLateral')}
        items={rubric.lateral_directions}
        kind="sideways"
      />

      <div className="rubric-view-block">
        <h3 className="eyebrow">{t('profile.rubricCommute')}</h3>
        <CommuteRow commute={rubric.commute} />
      </div>
    </section>
  )
}

/** What a rule does to a job. Drives the gutter marker and its colour. */
type RuleKind = 'require' | 'reject' | 'both' | 'sideways'

/** The mark for each `RuleKind` (PIVOT-139) — replaces the four `::before`
 * glyphs (`+`/`×`/`±`/`→`) this file used to draw with a CSS `content`
 * string and nothing behind it. `sideways` reuses `arrow-turn`, the mark
 * built from `Landing.css`'s `.landing-turn-arc` geometry, so the lateral
 * direction a candidate could pivot to reads with the same shape as the
 * brand's own "turn" motif. */
const RULE_ICON: Record<RuleKind, IconName> = {
  require: 'check',
  reject: 'cross',
  both: 'plus-minus',
  sideways: 'arrow-turn',
}

/**
 * One list of rules, in the shared row grammar: a marker in the gutter, one
 * rule per row. Every list on this screen uses it, so a reader learns the
 * shape once — and `kind` is the only thing that varies, because what a rule
 * does to a job is the only thing that actually differs between them.
 */
function RuleList({
  label,
  items,
  kind,
  help,
}: {
  label: string
  items: string[]
  kind: RuleKind
  help?: string
}) {
  if (items.length === 0) return null
  return (
    <div className="rubric-view-block">
      <h3 className="eyebrow">{label}</h3>
      {help && <p className="rubric-view-help">{help}</p>}
      <ul className={`rubric-view-rules rubric-view-rules--${kind}`}>
        {items.map((item) => (
          <li key={item}>
            <Icon name={RULE_ICON[kind]} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * One axis's slot in the three-up legend row: its share of the total weight,
 * next to its name. The rail *is* the comparison this makes — no description
 * here, so the row stays three-up at any width (PIVOT-145). Its description
 * lives in the stacked `AxisNote` below instead.
 *
 * The share is shown rather than the stored weight. `1.4` means nothing on its
 * own — the claim the rubric is making is that values outrank pay, and only
 * the proportion says that.
 */
function AxisShare({ label, share }: { label: string; share: number }) {
  const { t } = useI18n()
  return (
    <li>
      <span className="rubric-view-share">{t('profile.rubricShare', { share })}</span>
      <span className="rubric-view-axis-label">{label}</span>
    </li>
  )
}

/**
 * One axis's description, stacked below the three-up legend row rather than
 * inside it (PIVOT-145) — a real LLM description runs to hundreds of
 * characters, which a narrow legend column padded every axis to the tallest
 * of the three and still wrapped word-by-word. The colour-coded rule that
 * used to live on the legend item's `border-top` moves to this row's
 * `border-left` instead, so the segment-to-axis link survives the move.
 */
function AxisNote({ label, description }: { label: string; description: string }) {
  return (
    <li>
      <span className="rubric-view-axis-label">{label}</span>
      <p>{description}</p>
    </li>
  )
}

/**
 * One strength. A claim with no evidence says so rather than looking proven —
 * the backend leaves `evidence` empty instead of inventing a number
 * (constitution §VII), and hiding that here would undo the point.
 */
function StrengthRow({ strength }: { strength: Strength }) {
  const { t } = useI18n()
  return (
    <li>
      <span className="rubric-view-claim">{strength.claim}</span>
      {strength.evidence ? (
        <span className="rubric-view-evidence">{strength.evidence}</span>
      ) : (
        <span className="rubric-view-evidence rubric-view-evidence--missing">
          {t('profile.rubricNoEvidence')}
        </span>
      )}
    </li>
  )
}

/** The commute rule as labelled pills; "not stated" when the candidate gave none. */
function CommuteRow({ commute }: { commute: CommuteRule }) {
  const { t } = useI18n()
  const parts: { label: string; value: string }[] = []
  if (commute.anchor) parts.push({ label: t('profile.rubricCommuteFrom'), value: commute.anchor })
  if (commute.max_minutes !== null) {
    parts.push({
      label: t('profile.rubricCommuteWithin', { minutes: String(commute.max_minutes) }),
      value: '',
    })
  }
  if (commute.modes.length > 0) {
    parts.push({ label: t('profile.rubricCommuteBy'), value: commute.modes.join(', ') })
  }
  if (commute.regions.length > 0) {
    parts.push({ label: t('profile.rubricCommuteRegions'), value: commute.regions.join(', ') })
  }
  if (commute.examples.length > 0) {
    parts.push({ label: t('profile.rubricCommuteExamples'), value: commute.examples.join(', ') })
  }

  if (parts.length === 0) return <p className="rubric-view-help">{t('profile.rubricNotStated')}</p>

  return (
    <dl className="rubric-view-commute">
      {parts.map((part) => (
        <div key={part.label}>
          <dt>{part.label}</dt>
          <dd>{part.value}</dd>
        </div>
      ))}
    </dl>
  )
}
